import { useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { toast } from "react-toastify";
import * as Fa from "react-icons/fa";
import type { ConstructFormData, ConstructType, KpiRow } from "./Construct";
import {
  uploadTabData,
  downloadTabData,
  downloadTabTemplate,
} from "./uploadDownloadApi";
import { downloadExcelDateFormat } from "../../../Services/GlobalFunction/DownLoadExcel";

interface Props {
  formData: ConstructFormData;
  setFormData: React.Dispatch<React.SetStateAction<ConstructFormData>>;
  constructType: ConstructType;
  constructSeq: number | null;
}

const toKey = (ct: ConstructType) =>
  ct === "B2C" ? "b2c" : ct === "HDSSD" ? "hsssd" : "industrial";


const pillBtn = (bg: string, disabled = false): React.CSSProperties => ({
  backgroundColor: disabled ? "#bbb" : bg,
  color: "#fff",
  border: "none",
  borderRadius: "20px",
  padding: "7px 20px",
  fontWeight: 700,
  fontSize: "0.82rem",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1,
  boxShadow: disabled ? "none" : "0 2px 6px rgba(0,0,0,0.22)",
  whiteSpace: "nowrap" as const,
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
});

interface RowState {
  fileName: string;
  file: File | null;
  status: "idle" | "uploading" | "uploaded" | "error";
  error: string;
  errorRows: Record<string, unknown>[] | null;
}

const emptyRow = (): RowState => ({
  fileName: "",
  file: null,
  status: "idle",
  error: "",
  errorRows: null,
});

export default function ProdCap({
  formData,
  constructType,
  constructSeq,
}: Props) {
  const key = toKey(constructType);
  const kpis: KpiRow[] = formData[key].kpis.filter((k) => k.paramId === 1);

  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const UserId = localStorage.getItem("UserId")?.toString() ?? "";

  const [rowStates, setRowStates] = useState<Record<string, RowState>>(() =>
    Object.fromEntries(kpis.map((k) => [k.id, emptyRow()])),
  );

  const patch = (kpiId: string, update: Partial<RowState>) =>
    setRowStates((prev) => ({
      ...prev,
      [kpiId]: { ...prev[kpiId], ...update },
    }));

  /* ── File selected → store File object, reset status ── */
  const handleFileChange = (
    kpiId: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isXlsx =
      file.name.endsWith(".xlsx") ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    if (!isXlsx) {
      patch(kpiId, {
        fileName: file.name,
        file: null,
        status: "idle",
        error: "Please select a valid .xlsx file",
        errorRows: null,
      });
      e.target.value = "";
      return;
    }
    patch(kpiId, {
      fileName: file.name,
      file,
      status: "idle",
      error: "",
      errorRows: null,
    });
    e.target.value = "";
  };

  /* ── Upload button click — single call: validate + save ── */
  const handleUploadClick = async (kpi: KpiRow) => {
    const row = rowStates[kpi.id] ?? emptyRow();

    if (!row.file) {
      patch(kpi.id, { error: "Please select an .xlsx file first" });
      return;
    }

    patch(kpi.id, { status: "uploading", error: "", errorRows: null });

    const result = await uploadTabData({
      paramId: kpi.paramId,
      constructSeq,
      constructType,
      file: row.file,
      userId: UserId,
      tabname: "ProdCapping",
    });

    if (result.outcome === "uploaded") {
      patch(kpi.id, { status: "uploaded", error: "" });
    } else if (result.outcome === "warning") {
      toast.warn(result.message);
      patch(kpi.id, { status: "idle", error: "" });
    } else {
      // "error"
      patch(kpi.id, {
        status: "error",
        error: result.message,
        errorRows: result.errorRows,
      });
    }
  };

  /* ── Download error rows as xlsx ── */
  const handleDownloadErrorFile = (kpiId: string) => {
    const errorRows = rowStates[kpiId]?.errorRows;
    if (!errorRows || errorRows.length === 0) return;
    downloadExcelDateFormat(errorRows, `ProdCapping_Errors_${Date.now()}.xlsx`);
  };

  /* ── Download Template → common API ── */
  const handleDownloadTemplate = (kpi: KpiRow) => {
    downloadTabTemplate({
      tabName: "ProdCapping",
      paramId: kpi.paramId,
      parameterName: kpi.parameter,
      constructType,
    });
  };

  /* ── Download Capping Data → common API ── */
  const handleDownloadCappingData = (kpi: KpiRow) => {
    downloadTabData({
      tabName: "ProdCapping",
      paramId: kpi.paramId,
      parameterName: kpi.parameter,
      constructSeq,
      constructType,
    });
  };

  /* ── Upload button label ── */
  const uploadBtnLabel = (status: RowState["status"]) => {
    switch (status) {
      case "uploading":
        return "Uploading…";
      case "uploaded":
        return "Uploaded";
      case "error":
        return "Retry";
      default:
        return "Upload";
    }
  };

  const uploadBtnBg = (status: RowState["status"]) =>
    status === "uploaded" ? "#2e8b57" : "#1a7a3c";

  const isBusy = (status: RowState["status"]) => status === "uploading";

  return (
    <div style={{ padding: "4px 0" }}>
      <div
        style={{
          marginBottom: "14px",
          padding: "8px 14px",
          background: "#f0f9f4",
          border: "1px solid #b2dfcc",
          borderRadius: "4px",
          fontSize: "0.84rem",
          color: "#1a5c30",
          fontStyle: "italic",
        }}
      >
        Either LBM or PV or SKU code can be uploaded for a selected Construct
      </div>

      <div className="DataTable">
        <DataTable
          value={kpis}
          stripedRows
          responsiveLayout="scroll"
          style={{ width: "100%" }}
        >
          <Column
            field="parameter"
            header="Parameter"
            style={{ width: "22%" }}
            body={(rowData) => <span>{rowData.parameter}</span>}
          />
          <Column
            header="Target Upload"
            style={{ width: "50%" }}
            body={(kpi) => {
              const row = rowStates[kpi.id] ?? emptyRow();
              const busy = isBusy(row.status);
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  {/* Upload Controls */}
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 0 }}
                  >
                    <input
                      type="text"
                      readOnly
                      value={row.fileName}
                      placeholder="No file selected"
                      style={{
                        width: "220px",
                        padding: "6px 8px",
                        border: "1px solid #bbb",
                        borderRight: "none",
                        fontSize: "0.82rem",
                        backgroundColor: "#fff",
                        color: row.fileName ? "#333" : "#999",
                        borderRadius: "2px 0 0 2px",
                      }}
                    />

                    <button
                      onClick={() => fileRefs.current[kpi.id]?.click()}
                      disabled={busy}
                      style={{
                        padding: "6px 14px",
                        fontSize: "0.82rem",
                        border: "1px solid #bbb",
                        borderRight: "none",
                        backgroundColor: "#f0f0f0",
                        cursor: busy ? "not-allowed" : "pointer",
                        fontWeight: 600,
                      }}
                    >
                      Select
                    </button>

                    <button
                      onClick={() => handleUploadClick(kpi)}
                      disabled={busy}
                      style={{
                        ...pillBtn(uploadBtnBg(row.status), busy),
                        borderRadius: "0 4px 4px 0",
                        padding: "6px 16px",
                        fontSize: "0.82rem",
                      }}
                    >
                      {uploadBtnLabel(row.status)}
                    </button>

                    <input
                      type="file"
                      accept=".xlsx"
                      style={{ display: "none" }}
                      ref={(el) => (fileRefs.current[kpi.id] = el)}
                      onChange={(e) => handleFileChange(kpi.id, e)}
                    />
                  </div>

                  {row.status === "uploaded" && (
                    <span
                      style={{
                        fontSize: "0.78rem",
                        color: "#1a7a3c",
                        fontWeight: 600,
                      }}
                    >
                      Uploaded successfully
                    </span>
                  )}

                  <div className="row">
                    <div className="col-lg-6 col-md-12 col-sm-12">
                      {row.status === "idle" && !row.fileName && (
                        <p
                          className="mb-0"
                          style={{
                            fontSize: "0.78rem",
                            color: "#888",
                            fontStyle: "italic",
                          }}
                        >
                          (Select an .xlsx file to upload)
                        </p>
                      )}
                      {row.error && (
                        <p
                          className="mb-0"
                          style={{ fontSize: "0.78rem", color: "#c0392b" }}
                        >
                          {row.error}
                        </p>
                      )}
                    </div>
                    <div className="col-lg-6 col-md-12 col-sm-12">
                      {row.errorRows && row.errorRows.length > 0 && (
                        <button
                          onClick={() => handleDownloadErrorFile(kpi.id)}
                          className="btn btn-danger btn-sm"
                          style={{ fontSize: "12px" }}
                        >
                          Error File <Fa.FaDownload className="ms-2" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            }}
          />

          <Column
            header="Actions"
            style={{ width: "32%" }}
            body={(kpi) => {
              return (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <button
                    onClick={() => handleDownloadTemplate(kpi)}
                    className="btn btn-danger btn-sm"
                    style={{ fontSize: "12px" }}
                  >
                    Template <Fa.FaDownload className="ms-2" />
                  </button>

                  <button
                    onClick={() => handleDownloadCappingData(kpi)}
                    className="btn btn-success btn-sm"
                    style={{ fontSize: "12px" }}
                  >
                    Capping Data <Fa.FaDownload className="ms-2" />
                  </button>
                </div>
              );
            }}
          />
        </DataTable>
      </div>
    </div>
  );
}
